import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Lock, LogOut } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { membershipAPI } from '../lib/api';

// s159 — when a member's free trial or paid membership is over, the whole site
// is locked behind this modal on the HOME page until they pay. Any other page
// (My Account, admin links, …) and any product (the KMS redirects here with
// ?membership=expired) lands on "/" first, so there is only ONE pay surface.
// The server keeps refusing My Account APIs on its own (get_current_member).

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

export default function MembershipGate() {
  const { payPass, leavePayMode, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const lapsed = !!payPass;
  const [offer, setOffer] = useState(null);
  const [plan, setPlan] = useState('monthly');
  const [code, setCode] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (lapsed && location.pathname !== '/') navigate('/', { replace: true });
  }, [lapsed, location.pathname, navigate]);
  useEffect(() => {
    if (lapsed && !offer) membershipAPI.offer().then(r => setOffer(r.data)).catch(() => setOffer({ error: true }));
  }, [lapsed, offer]);
  useEffect(() => {
    if (!lapsed) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [lapsed]);

  if (!lapsed || location.pathname !== '/') return null;
  const trial = (() => { try { return sessionStorage.getItem('mb_pay_src') === 'trial'; } catch (_) { return false; } })();
  const q = coupon ? coupon.quote : offer?.quotes?.[plan];
  const applyCoupon = async () => {
    if (!code.trim()) { setMsg('Enter a coupon code'); return; }
    try {
      const { data } = await membershipAPI.validateCoupon(code.trim(), plan);
      if (!data.ok) { setCoupon(null); setMsg(data.message); return; }
      setCoupon(data); setMsg(`Coupon ${data.code} applied`);
    } catch { setMsg('Could not check the coupon'); }
  };
  const pay = async () => {
    setBusy(true); setMsg('');
    try {
      const { data } = await membershipAPI.checkout(plan, coupon ? coupon.code : '');
      if (data.url) {
        // s159 — leave pay mode BEFORE redirecting: otherwise the welcome page
        // sees mb_pay still in sessionStorage and MembershipGate's own
        // "lapsed must be on /" guard bounces the user straight back to the
        // modal, so a successful payment looks like it did nothing.
        leavePayMode();
        window.location.href = data.url;
        return;
      }
      setMsg(data.message || 'Payments are not available yet');
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) { setMsg('Your session to pay expired. Log in again.'); }
      else setMsg('Could not start the payment');
    }
    setBusy(false);
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="mg-title" data-testid="membership-gate"
      className="fixed inset-0 z-[1000] grid place-items-center p-4" style={{ background: 'rgba(10,12,20,.72)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-md rounded-2xl bg-white p-7 shadow-2xl" style={{ fontFamily: "'Inter', sans-serif" }}>
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-full" style={{ background: 'rgba(15,23,42,.06)' }}>
          <Lock className="h-5 w-5 text-slate-700" />
        </span>
        <h2 id="mg-title" className="mt-4 text-2xl font-bold text-slate-900 tracking-tight">
          {trial ? 'Your free period has ended' : 'Your membership has expired'}
        </h2>
        <p className="mt-2 text-[15px] leading-relaxed text-slate-600">
          {trial ? 'To keep using the platform, its products and your community, choose a plan for your membership.'
            : 'Renew your membership to get your access back. Your profile and community are saved.'}
        </p>
        {!offer && <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
        {offer && !offer.error && (<>
          <p className="mt-5 text-sm font-semibold text-slate-900">{offer.name}</p>
          <div className="mt-2 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Plan">
            {['monthly', 'annual'].map(p => (
              <button key={p} type="button" role="radio" aria-checked={plan === p}
                onClick={() => { setPlan(p); setCoupon(null); setMsg(''); }}
                className="rounded-xl border px-4 py-3 text-left transition-colors"
                style={plan === p ? { borderColor: '#0f172a', background: '#f8fafc' } : { borderColor: 'rgba(15,23,42,.15)' }}>
                <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">{p === 'monthly' ? 'Monthly' : 'Annual'}</span>
                <span className="block text-xl font-bold text-slate-900 tabular-nums">{money(offer.prices?.[p])}</span>
              </button>
            ))}
          </div>
          <form noValidate onSubmit={e => { e.preventDefault(); e.stopPropagation(); applyCoupon(); }} className="mt-3 flex gap-2">
            <input value={code} onChange={e => setCode(e.target.value)} placeholder="Coupon code" aria-label="Coupon code"
              autoComplete="off" autoCorrect="off" autoCapitalize="characters" spellCheck={false} name="mg_coupon"
              className="h-10 min-w-0 flex-1 rounded-lg border border-slate-200 px-3 text-sm uppercase" />
            <button type="submit" className="h-10 rounded-lg border border-slate-200 px-4 text-sm font-semibold">Apply</button>
          </form>
          {q && <p className={`mt-3 text-sm ${coupon ? 'text-red-600' : 'text-slate-600'}`}>Today <b className={`tabular-nums ${coupon ? 'text-red-600' : 'text-slate-900'}`}>{money(q.today)}</b>{q.then > 0 ? <>, then {money(q.then)} / {plan === 'annual' ? 'year' : 'month'}</> : null}</p>}
          {msg && <p className="mt-2 text-sm font-medium text-red-600" role="status">{msg}</p>}
          <button type="button" onClick={pay} disabled={busy}
            className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-xl text-[15px] font-bold text-white transition-transform active:scale-[.98] disabled:opacity-60"
            style={{ background: '#0f172a' }} data-testid="membership-gate-pay">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}Pay and continue
          </button>
        </>)}
        {offer?.error && <p className="mt-5 text-sm text-red-600">Could not load the plans. Reload the page.</p>}
        <a href="/" onClick={(e) => {
          e.stopPropagation();
          leavePayMode();
          // Full navigation (not a React route change) so AuthProvider re-mounts
          // without any stale pay state. preventDefault only to swap href for
          // location.assign — same net effect, bypasses any delegated click
          // listener upstream that might read e.target.closest('a').
          e.preventDefault();
          window.location.assign('/');
        }} className="mt-4 inline-flex w-full items-center justify-center gap-1.5 text-sm text-slate-500" data-testid="membership-gate-notnow">
          <LogOut className="h-4 w-4" />Not now
        </a>
      </div>
    </div>
  );
}
